import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Handle initial state on page load (e.g., when URL contains variant parameter)
    this.#handleInitialVariantState();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    // What are we missing?
    // - need to support swatches potentially, we will need to check on what's needed for that.

    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // Pass collection context for size filtering (newest-releases-men, newest-releases-women, etc.)
    // Use the current browser URL path which includes the collection handle
    const currentPath = window.location.pathname;
    let collectionFilter = '';
    if (currentPath.includes('newest-releases-men')) {
      collectionFilter = 'newest-releases-men';
    } else if (currentPath.includes('newest-releases-women')) {
      collectionFilter = 'newest-releases-women';
    } else if (currentPath.includes('12-12-sale-duckboots')) {
      collectionFilter = '12-12-sale-duckboots';
    }

    // If we have a collection filter, add it to params
    if (collectionFilter) {
      params.push(`collection_filter=${collectionFilter}`);

      // Also use the full current path (with collection) as the productUrl for the request
      // This ensures the server-side Liquid sees the collection in request.path
      if (!productUrl.includes('/collections/')) {
        productUrl = currentPath;
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Updates the inventory status in the closest custom title inventory container.
   * @param {Document} newHtml - The new HTML document.
   */
  updateInventoryStatus(newHtml) {
    // Find the closest custom title inventory container by traversing up the DOM
    let inventoryContainer = null;
    let currentElement = this.parentElement;

    // Traverse up the DOM tree to find the inventory container
    while (currentElement && !inventoryContainer) {
      inventoryContainer = currentElement.querySelector('.custom-title-inventory-container');
      if (!inventoryContainer) {
        currentElement = currentElement.parentElement;
      }
    }

    // If not found in parents, try siblings and their descendants
    if (!inventoryContainer) {
      const parent = this.parentElement;
      if (parent) {
        inventoryContainer = parent.querySelector('.custom-title-inventory-container');
      }
    }

    if (inventoryContainer) {
      // Find the new inventory status from the updated HTML
      const newInventoryContainer = newHtml.querySelector('.custom-title-inventory-container');

      if (newInventoryContainer) {
        // Find the availability spans in both old and new containers
        const currentAvailabilitySpan = inventoryContainer.querySelector('.available, .unavailable');
        const newAvailabilitySpan = newInventoryContainer.querySelector('.available, .unavailable');

        if (currentAvailabilitySpan && newAvailabilitySpan) {
          // Update the class and text content
          currentAvailabilitySpan.className = newAvailabilitySpan.className;
          currentAvailabilitySpan.textContent = newAvailabilitySpan.textContent;

          console.log('Inventory status updated:', newAvailabilitySpan.textContent);
        } else {
          console.log('Availability span not found in one of the containers');
        }
      } else {
        console.log('New inventory container not found in updated HTML');
      }
    } else {
      console.log('Custom title inventory container not found');
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = true) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Update inventory status after variant picker update
          this.updateInventoryStatus(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // The re-fetched markup comes from the bundled product's own standalone page, rendered
    // through its normal (non-bundle) variant picker — so unavailable options that
    // bundle-item.liquid asked to hide come back on every variant change. Strip them again
    // from the fetched fragment before it's morphed in, scoped to bundle item pickers only.
    if (this.closest('bundle-item-picker') && newVariantPickerSource instanceof HTMLElement) {
      newVariantPickerSource.querySelectorAll('[data-option-available="false"]').forEach((element) => {
        (element.closest('.variant-option__values-wrapper') ?? element).remove();
      });
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Handles initial variant state on page load.
   * This ensures button states are correct when URL contains variant parameter.
   */
  #handleInitialVariantState() {
    // Check if we're on a product page and have a selected variant
    if (Theme.template.name !== 'product') return;

    // Skip if inside a dialog (e.g. quick-add modal) to avoid dispatching
    // events with the PDP's document HTML, which would overwrite the
    // quick-add product's price with the PDP product's price.
    if (this.closest('dialog')) return;

    const selectedOption = this.selectedOption;
    if (!selectedOption) return;

    // Get the variant data from the JSON script tag within this variant picker
    const variantScript = this.querySelector('script[type="application/json"]');
    if (!variantScript || !variantScript.textContent) return;

    try {
      const variantData = JSON.parse(variantScript.textContent);

      // Dispatch the variant update event to ensure all components are in sync
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(variantData, this.selectedOptionId, {
            html: document,
            productId: this.dataset.productId ?? '',
          })
        );
      }
    } catch (error) {
      console.error('Failed to parse initial variant data:', error);
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
